import React, { useState } from "react";
import { Box, Button, Grid, Paper, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";
import { SelectElement, TextFieldElement, useForm } from "react-hook-form-mui";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useErrorLogging } from "../hooks/useErrorLogging";
import { API, graphqlOperation } from "aws-amplify";
import { getLLMCustomer } from "../graphql/queries";
import { GetLLMCustomerQuery } from "../API";
import { GraphQLResult } from "@aws-amplify/api";
import { updateLLMCustomerAfterEdit } from "../dataaccess/updateCustomerAfterEdit";
import { createLLMCustomerAfterEdit } from "../dataaccess/createLLMCustomerAfterEdit";
import { fetchLLMUsageTiers } from "../dataaccess/fetchLLMUsageTiers";
import { useUser } from "../contexts/useUser";

const schema = z.object({
  id: z.string().optional(),
  projectId: z.string().optional(),
  name: z.string().min(1, "Name is required").optional().nullable(),
  status: z.string().min(1, "Status is required"),
  loadSheddingTier: z.number().int().min(1).max(4),
  dataResidency: z.string(),
  enterpriseUser: z.boolean().optional().nullable(),
  usageTierId: z.string().optional().nullable(),
});

type CustomerFormType = z.infer<typeof schema>;

const CustomerForm = () => {
  const { id } = useParams<{ id: string }>();
  const { project } = useUser();
  const navigate = useNavigate();
  const { logError } = useErrorLogging();

  const queryClient = useQueryClient();
  const mutationUpdateCustomer = useMutation({
    mutationFn: updateLLMCustomerAfterEdit,
    onSuccess: (result, vals) => {
      queryClient.invalidateQueries({ queryKey: ["customers", { id: id }] });

      window.history && window.history.length > 1
        ? navigate(-1)
        : navigate("/");
    },
    onError: (error) => {
      logError("Error updating customer", error as any);
    },
  });

  const mutationCreateCustomer = useMutation({
    mutationFn: createLLMCustomerAfterEdit,
    onSuccess: (result, vals) => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      window.history && window.history.length > 1
        ? navigate(-1)
        : navigate("/");
    },
    onError: (error) => {
      logError("Error creating customer", error as any);
    },
  });

  const {
    data: customerData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["customers", { id: id }],
    queryFn: () =>
      API.graphql(graphqlOperation(getLLMCustomer, { id: id })) as Promise<
        GraphQLResult<GetLLMCustomerQuery>
      >,
    enabled: !!id,
  });

  const {
    data: usageTiersData,
    isLoading: usageTiersLoading,
  } = useQuery({
    queryKey: ["usageTiers", { projectId: project?.id }],
    queryFn: () => fetchLLMUsageTiers(project?.id || ""),
    enabled: !!project?.id,
  });

  const { handleSubmit, control, reset, watch, getValues } = useForm<CustomerFormType>({
    values: customerData?.data?.getLLMCustomer ? {
      ...customerData.data.getLLMCustomer,
    } : {
      projectId: project?.id,
      name: "",
      status: "active",
      loadSheddingTier: project?.newCustomerLoadSheddingTier || 2,
      dataResidency: "global",
      usageTierId: "",
    },
    resolver: zodResolver(schema),
  });

  if (id && isLoading) return <div>Loading...</div>;
  if (id && isError) return <div>Error loading project</div>;

  const handleUpdate = (values: CustomerFormType) => {
    if (!values) return;

    try {
      mutationUpdateCustomer.mutate({ id: id, ...values } as any);
    } catch (error) {
      logError("Error updating customer", error as any);
    }
  };

  const handleCreate = (values: CustomerFormType) => {
    if (!values) return;
    if (!project) return;

    mutationCreateCustomer.mutate({
      projectId: project?.id,
      tokensRemaining: project?.newCustomerTokenCount || 0, // Legacy field - required by API
      ...values,
    });
  };

  const handleClose = () => {
    window.history && window.history.length > 1 ? navigate(-1) : navigate("/");
  };

  const onSubmit = (data: CustomerFormType) => {
    if (id) handleUpdate(data);
    else handleCreate(data);
  };

  return (
     <Paper elevation={3} style={{ borderRadius: 3, border: "1px solid #444", margin:2, padding:2 }}>
      <Typography padding={2} variant="h6">
        Customer
      </Typography>
      <Grid container padding={2} spacing={2}>
        <Grid item xs={12} md={6}>
          <TextFieldElement
            control={control}
            name="name"
            label="Name"
            size="small"
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectElement
            control={control}
            name="status"
            label="Customer status"
            size="small"
            required
            fullWidth
            options={[
              { id: "active", label: "Active" },
              { id: "disabled", label: "Disabled" },
              {
                id: "errors",
                label: "Disabled (due to errors)",
              },
              { id: "deleted", label: "Deleted" },
            ]}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SelectElement
            control={control}
            name="loadSheddingTier"
            label="Load Shedding Tier"
            size="small"
            required
            fullWidth
            options={[
              { id: 1, label: "Tier 1 (first to shed)" },
              {
                id: 2,
                label: "Tier 2 (customers shed after Tier 1)",
              },
              { id: 3, label: "Tier 3 customers (shed as last resort)" },
              { id: 4, label: "Never shed" },
            ]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectElement
            name="dataResidency"
            label="Data Residency Restriction"
            control={control}
            required
            size="small"
            fullWidth
            options={[
              { id: "global", label: "Global - no restrictions" },
              { id: "americas", label: "North / Central / South America" },
              { id: "eu", label: "European Union (Member States of the EU)" },
              { id: "noneu", label: "Europe (Non-member states of the EU)" },
              { id: "apac", label: "Asia / Pacific / Oceania" },
              { id: "other", label: "Other" },
            ]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectElement
            control={control}
            name="enterpriseUser"
            label="Allowed Enterprise User Access"
            size="small"
            fullWidth
            options={[
              { id: true, label: "Yes" },
              { id: false, label: "No" },
            ]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          {(() => {
            const originalUsageTierId = customerData?.data?.getLLMCustomer?.usageTierId;
            const usageTierLocked = !!id && !!originalUsageTierId;
            return (
              <Box>
                <SelectElement
                  control={control}
                  name="usageTierId"
                  label="Usage Tier"
                  size="small"
                  fullWidth
                  disabled={usageTierLocked}
                  helperText={usageTierLocked ? "change in the usage tab" : undefined}
                  options={[
                    { id: "", label: "No Usage Tier" },
                    ...(usageTiersData?.data?.usageTiersByProjectId?.items?.map((tier) => ({
                      id: tier?.id || "",
                      label: `${tier?.name || ""} (v${tier?.version || "1"})`,
                    })) || []),
                  ]}
                />
                {/* If we ever want a more stylized caption than helperText */}
                {/* {usageTierLocked && (
                  <Typography variant="caption" color="text.secondary">change in the usage tab</Typography>
                )} */}
              </Box>
            );
          })()}
        </Grid>
      </Grid>
      <Box sx={{ padding: 2 }}>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant={"contained"}
          sx={{ marginRight: 2 }}
        >
          Save and Close
        </Button>
        <Button onClick={() => reset()} variant={"outlined"}>
          Reset
        </Button>
        <Button onClick={handleClose} variant={"outlined"}>
          Cancel
        </Button>
      </Box>

    </Paper>
  );
};

export default CustomerForm;
